import clsx from 'clsx';
import { Component, Input, Output, EventEmitter, HostBinding, OnInit } from '@angular/core';
import { parse, isValidNumber, CountryCode } from 'libphonenumber-js';
import { COUNTRY_CODE_OTHER, COUNTRY_LIST } from './cellphone.constants';
import { DxiValidationRuleModule } from 'devextreme-angular/ui/nested';
import { DxValidatorModule } from 'devextreme-angular/ui/validator';
import { DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxSelectBoxModule } from 'devextreme-angular/ui/select-box';
import { NgClass } from '@angular/common';

export type CellphoneLabelSize = 'xs' | 'sm' | 'md';

export interface ICountry {
  code: string;
  flag: string;
  name: string;
  countryPrefix: string;
  flagImage: string;
}

@Component({
    selector: 'pxw-cellphone',
    templateUrl: './cellphone.component.html',
    styleUrls: ['./cellphone.component.scss'],
    imports: [
        NgClass,
        DxSelectBoxModule,
        DxTemplateModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxiValidationRuleModule
    ]
})
export class CellphoneComponent implements OnInit {
  @Input() countryCode: string | undefined;
  @Input() label = '';
  @Input() placeholder = '';
  @Input() labelSize: CellphoneLabelSize = 'sm';
  @Input() phoneNumber: string | null = null;
  @Input() disabled = false;
  @Input() min: number | undefined;
  @Input() max: number | undefined;
  @Input() helper: string | null = null;
  @Input() invalidMessage: string | undefined;
  @Input() value: string | undefined;
  @Input() required = false;
  @Input() stylingMode: 'outlined' | 'underlined' | 'filled' = 'outlined';

  @Input() disabledCountryCode = false;
  @Input() countrySelected: ICountry | undefined;

  // This probably should be moved to a dedicated component, e.g.: pxw-validation
  @Input() validationOnly = false;
  @Output() valueChange = new EventEmitter();
  @Output() countryCodeChange = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur = new EventEmitter();
  @Output() keyPress = new EventEmitter();
  @Output() keyDown = new EventEmitter();
  @Output() keyUp = new EventEmitter();

  countries: ICountry[] = COUNTRY_LIST;
  displayCountryDropdown = true;
  mask: string | undefined;
  validateCellphoneFn: (({ value }: { value: string }) => boolean) | undefined;

  @HostBinding('class') get classNames() {
    return clsx('cellphone', 'ui-input', `ui-input--${this.stylingMode}`, {
      'ui-input--validation-only': this.validationOnly,
      'ui-input--with-helper': this.helper,
      'ui-input--disabled-country-code': this.disabledCountryCode,
    });
  }

  ngOnInit() {
    this.validateCellphoneFn = this.validateCellphone.bind(this);

    if (this.countrySelected) {
      if (this.countrySelected.code === 'XX') {
        this.helper = '¡No olvides incluir el código de área de tu país y ciudad!';
        this.displayCountryDropdown = false;
      }
      this.countryCode = this.countrySelected.code;
    } else {
      this.countryCode = this.countries[0].code;
    }

    if (this.value) {
      if (this.countrySelected) {
        const value = String(this.value);

        this.phoneNumber = value.substr(this.countrySelected.countryPrefix.length);
      } else {
        const value = String(this.value);

        const matchedCountry = this.countries.find(country =>
          value.startsWith(country.countryPrefix),
        );
        const countryPrefix = matchedCountry ? matchedCountry.countryPrefix : '';

        this.countryCode = matchedCountry ? matchedCountry.code : '';
        this.phoneNumber = matchedCountry ? value.substr(countryPrefix.length) : value;
      }

      setTimeout(() => {
        this.countryCodeChange.emit(this.countryCode);
      });
    }
  }

  get hasRules() {
    return true;
  }

  get selectedCountry() {
    return this.countries.find(country => country.code === this.countryCode);
  }

  validateCellphone({ value }: { value: string }) {
    if (!value && !this.required) {
      return true;
    }
    if (!this.countryCode || this.countryCode === COUNTRY_CODE_OTHER) {
      const length = (value || '').length;

      return length >= 11 && length <= 15;
    }
    try {
      const numberObject = parse(value, this.countryCode as CountryCode);

      return isValidNumber(numberObject);
    } catch (e) {
      return false;
    }
  }

  onCountryCodeChanged(value: string) {
    this.countryCode = value;

    this.countryCodeChange.emit(this.countryCode);
    this.phoneNumber = null;

    this.onPhoneNumberChanged(null);
  }

  onPhoneNumberChanged(value: string | null) {
    value = (value || '').replace(/\D/g, '');

    if (this.validateCellphone({ value })) {
      this.valueChange.emit(`${this.selectedCountry?.countryPrefix}${value}`);
    } else {
      this.valueChange.emit(null);
    }
    if (this.phoneNumber !== value) {
      setTimeout(() => {
        this.phoneNumber = value;
      });
    }
  }

  onBlur($event: any) {
    this.blur.emit($event);
  }

  onKeyPress($event: any) {
    this.keyPress.emit($event);

    if ($event.key.length > 1) {
      return true;
    }

    return /[0-9+]/.test($event.key);
  }

  onKeyDown($event: any) {
    this.keyDown.emit($event);
  }

  onKeyUp($event: any) {
    this.keyUp.emit($event);
  }
}
